/**
 * B-1 訂單管理（GET /admin/orders 列表 + /admin/orders/:id 詳情）。
 */

import { apiFetch } from './core';

export const ORDER_STATES = [
  'HOLD',
  'PAYMENT_PENDING',
  'PAID',
  'EXPIRED',
  'CANCELLED',
  'PAYMENT_FAILED',
  'OVERSOLD',
] as const;
export type OrderState = (typeof ORDER_STATES)[number];

export const ORDER_CHANNELS = ['web', 'kiosk', 'partner', 'floor', 'recovery'] as const;
export type OrderChannel = (typeof ORDER_CHANNELS)[number];

export interface AdminOrderListItem {
  id: string;
  order_number: string;
  channel: string;
  state: OrderState;
  subtotal: string;
  total: string;
  item_count: number;
  first_product_name: Record<string, string> | null;
  created_at: string;
  updated_at: string;
}

export interface AdminOrderListResult {
  items: AdminOrderListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AppliedPricingRule {
  id: string;
  promotion_type: string;
  list_price: string;
  discount_pct: string;
  fixed_amount_off: string | null;
  applicable_channels: string[] | null;
}

export interface AdminOrderItem {
  id: string;
  product_id: string;
  product_name: Record<string, string> | null;
  product_sku: string | null;
  schedule_id: string;
  schedule_departure_at: string | null;
  quantity: number;
  /** 票種（adult / child / senior…）；混票種訂單少了它就分不出哪列是大人哪列是兒童 */
  ticket_type: string;
  unit_price: string;
  line_total: string;
  pricing_id: string | null;
  pricing_rule: AppliedPricingRule | null;
  /**
   * 這筆屬於哪張套票；null = 單買。
   *
   * 套票不建班次，一張套票在明細裡是 N 筆各指一個真實班次的列，各筆金額是打包價
   * 的分攤結果——單看對不上任何牌價。
   */
  package_product_id: string | null;
  package_sku: string | null;
  package_name: Record<string, string> | null;
}

export interface AdminOrderDetail {
  id: string;
  order_number: string;
  channel: string;
  state: OrderState;
  subtotal: string;
  total: string;
  idempotency_key: string;
  created_at: string;
  updated_at: string;
  hold_expires_at: string | null;
  items: AdminOrderItem[];
}

export interface ListAdminOrdersQuery {
  state?: OrderState;
  channel?: OrderChannel;
  from?: string;
  to?: string;
  keyword?: string;
  limit?: number;
  offset?: number;
}

export function listAdminOrders(q: ListAdminOrdersQuery = {}): Promise<AdminOrderListResult> {
  const qs = new URLSearchParams();
  if (q.state) qs.set('state', q.state);
  if (q.channel) qs.set('channel', q.channel);
  if (q.from) qs.set('from', q.from);
  if (q.to) qs.set('to', q.to);
  if (q.keyword) qs.set('keyword', q.keyword);
  if (q.limit !== undefined) qs.set('limit', String(q.limit));
  if (q.offset !== undefined) qs.set('offset', String(q.offset));
  const suffix = qs.toString();
  return apiFetch<AdminOrderListResult>(`/admin/orders${suffix ? `?${suffix}` : ''}`, {
    method: 'GET',
  });
}

export function getAdminOrder(id: string): Promise<AdminOrderDetail> {
  return apiFetch<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(id)}`, { method: 'GET' });
}
